import { ServiceLayout } from "@/components/ServiceLayout"
import { smartMobilityFAQs } from "@/data/faqData"

const SmartMobility = () => {
  return (
    <ServiceLayout
      title="Smart Mobility Solutions"
      subtitle="Intelligent Mobile Experiences"
      description="We build native and cross-platform apps embedded with predictive behavior modeling and edge-AI capabilities to engage users in real-time."
      heroImage="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=2600"
      faqs={smartMobilityFAQs}
      faqTitle="Smart Mobility FAQs"
      faqSubtitle="Common questions about AI-powered mobility and transportation solutions"
      features={[
        {
          title: "Edge-AI Processing",
          description:
            "Process critical data locally on the device for millisecond-latency decision making, ensuring your app is fast and responsive even without internet.",
        },
        {
          title: "Predictive UX",
          description:
            "Interfaces that adapt to user behavior in real-time, anticipating needs and simplifying navigation paths automatically.",
        },
        {
          title: "Cross-Platform Excellence",
          description:
            "Unified codebases using React Native and Flutter that deliver native-grade performance across iOS and Android ecosystems.",
        },
      ]}
      approach={{
        title: "Cognitive Mobile Engineering",
        description:
          "We don't just build apps; we architect digital extensions of your workforce's cognitive capabilities.",
        image:
          "https://images.unsplash.com/photo-1555421689-d68471e18963?auto=format&fit=crop&q=80&w=2600",
        content: [
          "We move beyond simple app development to engineer 'Cognitive Digital Extensions'. Our process begins with high-fidelity telemetry mapping, where we visualize not just user flows, but 'Cognitive Friction' points - micro-moments where users hesitate or abandon tasks.",
          "By integrating Edge-AI models directly into the mobile binary (using TensorFlow Lite or CoreML), we enable the application to learn user habits locally. This data remains on-device, preserving privacy while allowing the interface to self-optimize. For example, the app can predict the next likely action and pre-fetch data, reducing perceived latency to near zero.",
          "Finally, our 'Offline-First' mesh architecture ensures that critical business logic executes regardless of network connectivity, synchronizing with the cloud only when optimal. This creates a workforce tool that is resilient, intelligent, and perpetually improving.",
        ],
      }}
      benefits={[
        "99.9% App Crash-Free Rates",
        "Sub-100ms Interaction Latency",
        "Offline-First Architecture",
        "Biometric Security Integration",
        "Real-time Analytics Dashboard",
        "Automated CI/CD Deployment",
      ]}
      process={{
        steps: [
          {
            title: "Discovery & Strategy",
            description:
              "We analyze your user personas and business goals to define the mobile product roadmap and technical architecture.",
          },
          {
            title: "UX/UI Design",
            description:
              "Creating high-fidelity prototypes and interaction flows that prioritize ease of use and accessibility.",
          },
          {
            title: "Agile Development",
            description:
              "Iterative sprints delivering shippable code every 2 weeks, with continuous testing and stakeholder feedback.",
          },
          {
            title: "Launch & Growth",
            description:
              "Store optimization (ASO), phased rollout, and integration of analytics for post-launch optimization.",
          },
        ],
      }}
      techStack={[
        {
          category: "Mobile Frameworks",
          items: ["React Native", "Flutter", "Swift (iOS)", "Kotlin (Android)"],
        },
        {
          category: "Edge AI",
          items: ["TensorFlow Lite", "CoreML", "PyTorch Mobile"],
        },
        {
          category: "Backend & Cloud",
          items: ["Node.js", "Firebase", "AWS Amplify", "GraphQL"],
        },
      ]}
    />
  )
}

export default SmartMobility
